// The head-to-head score from design/project/App12b-RivalryVersus and App22r-RevengeIncoming: how two players stand
// against each other, green when you lead and red when you trail.
import { useEffect, useState } from "react";
import { api } from "../api.js";
import { ALERT_OFF, MUTE, rivalOf, type Rival } from "../challenge.js";

/** Our record against this player, once the server has it. */
export function useRival(id: string | null | undefined): Rival | null {
  const [rival, setRival] = useState<Rival | null>(null);
  useEffect(() => {
    if (!id) return;
    let stop = false;
    void rivalOf(id).then((r) => !stop && setRival(r));
    return () => {
      stop = true;
    };
  }, [id]);
  return rival;
}

const UN = { fontFamily: "Unbounded, sans-serif", fontWeight: "900" } as const;
const MONO = { fontFamily: "'JetBrains Mono', monospace", fontWeight: "800" } as const;

export function leadColour(mine: number, theirs: number): string {
  return mine > theirs ? "#CCFF00" : mine < theirs ? "#FF3366" : "#FFFFFF";
}

/** "HEAD TO HEAD · YOU 3 – 2 MOONWOLF", with a note under it like "You lead". */
export function HeadToHead({ them, mine, theirs, note }: { them: string; mine: number; theirs: number; note: string }) {
  const lead = leadColour(mine, theirs);
  const name = { ...UN, fontSize: "11px", color: "#8B949E", maxWidth: "110px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
      <span style={{ ...UN, fontSize: "9px", letterSpacing: "0.14em", color: "#8B949E" }}>HEAD TO HEAD</span>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", maxWidth: "100%" }}>
        <span style={name}>YOU</span>
        <span style={{ ...MONO, fontSize: "30px", color: lead }}>{mine}</span>
        <span style={{ ...MONO, fontSize: "22px", color: "#4B535E" }}>–</span>
        <span style={{ ...MONO, fontSize: "30px", color: "#FFFFFF" }}>{theirs}</span>
        <span style={name}>{them.replace(/^@/, "").toUpperCase()}</span>
      </div>
      <span style={{ fontSize: "12px", color: lead, fontWeight: "700", textAlign: "center" }}>{note}</span>
    </div>
  );
}

/** "You lead", "They lead" or "All square". */
export function leadNote(mine: number, theirs: number): string {
  return mine > theirs ? "You lead" : mine < theirs ? "They lead" : "All square";
}

/**
 * "Stop revenge messages" in a bot message opens the app with the player to mute: the lobby does it and says so,
 * with a way to undo it.
 */
export function MuteNotice() {
  const [done, setDone] = useState<{ id: string; name: string | null; muted: boolean } | null>(null);
  useEffect(() => {
    const id = sessionStorage.getItem(MUTE);
    if (!id) return;
    sessionStorage.removeItem(MUTE);
    void api<{ muted: boolean; name: string | null }>(`/v1/rivals/${id}/mute`, { method: "POST", body: { on: true } })
      .then((r) => setDone({ id, name: r.name, muted: true }))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(null), 8_000);
    return () => clearTimeout(t);
  }, [done]);
  if (!done) return null;
  const undo = () =>
    void api(`/v1/rivals/${done.id}/mute`, { method: "POST", body: { on: !done.muted } })
      .then(() => setDone({ ...done, muted: !done.muted }))
      .catch(() => {});
  const who = done.name ?? "this player";
  return (
    <div role="status" style={{ position: "fixed", left: "12px", right: "12px", top: "calc(var(--tg-safe-area-inset-top, 47px) + var(--tg-content-safe-area-inset-top, 47px) + 8px)", zIndex: 45, maxWidth: "456px", margin: "0 auto", padding: "12px 14px", borderRadius: "16px", background: "#161B22", border: "1px solid #30363D", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", display: "flex", alignItems: "center", gap: "12px", fontFamily: "Inter, sans-serif", color: "#FFFFFF" }}>
      <span style={{ flex: "1", minWidth: "0", fontSize: "13px", lineHeight: "18px", overflowWrap: "anywhere" }}>{done.muted ? `Revenge messages from ${who} are off.` : `Revenge messages from ${who} are back on.`}</span>
      <button onClick={undo} style={{ flex: "none", minHeight: "36px", padding: "0 12px", borderRadius: "10px", border: "1px solid rgba(204,255,0,0.5)", background: "transparent", color: "#CCFF00", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}>{done.muted ? "Undo" : "Mute again"}</button>
    </div>
  );
}

/** "Stop waiting alerts" or "Stop power hour alerts" in a bot message: the lobby turns that alert off and says so. */
export function AlertNotice() {
  const [done, setDone] = useState<{ which: "waiting" | "power"; on: boolean } | null>(null);
  useEffect(() => {
    const which = sessionStorage.getItem(ALERT_OFF);
    if (which !== "waiting" && which !== "power") return;
    sessionStorage.removeItem(ALERT_OFF);
    void api("/v1/alerts", { method: "POST", body: { [which]: false } }).then(() => setDone({ which, on: false })).catch(() => {});
  }, []);
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(null), 8_000);
    return () => clearTimeout(t);
  }, [done]);
  if (!done) return null;
  const flip = () =>
    void api("/v1/alerts", { method: "POST", body: { [done.which]: !done.on } }).then(() => setDone({ ...done, on: !done.on })).catch(() => {});
  const what = done.which === "waiting" ? "Waiting alerts" : "Power hour alerts";
  return (
    <div role="status" style={{ position: "fixed", left: "12px", right: "12px", top: "calc(var(--tg-safe-area-inset-top, 47px) + var(--tg-content-safe-area-inset-top, 47px) + 8px)", zIndex: 45, maxWidth: "456px", margin: "0 auto", padding: "12px 14px", borderRadius: "16px", background: "#161B22", border: "1px solid #30363D", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", display: "flex", alignItems: "center", gap: "12px", fontFamily: "Inter, sans-serif", color: "#FFFFFF" }}>
      <span style={{ flex: "1", minWidth: "0", fontSize: "13px", lineHeight: "18px" }}>{done.on ? `${what} are back on.` : `${what} are off.`}</span>
      <button onClick={flip} style={{ flex: "none", minHeight: "36px", padding: "0 12px", borderRadius: "10px", border: "1px solid rgba(204,255,0,0.5)", background: "transparent", color: "#CCFF00", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}>{done.on ? "Turn off" : "Undo"}</button>
    </div>
  );
}
